'use client'
import React, { useRef, useImperativeHandle, useState } from 'react'
import { Heading } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { SpecsComparisonProps } from './types.js'

const componentType = 'specs'
const componentName = 'specs_comparison'

const SpecsComparison = React.forwardRef<HTMLDivElement, SpecsComparisonProps>(({ data, className }, ref) => {
  const { LeftMenu, RightMenu } = data || {}
  const [selectItem, setSelectItem] = useState('')
  const refs = useRef<Record<string, HTMLDivElement | null>>({})

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: 'Specs Comparison',
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  const normalNode = (v: string) => {
    if (!v) return null
    return (
      <div className="specs-comparison__value-wrapper break-all">
        <p
          className="specs-comparison__value min-xl:text-base min-xxl:text-lg text-sm font-bold text-[#1D1D1F]"
          dangerouslySetInnerHTML={{
            __html: v || '',
          }}
        />
      </div>
    )
  }

  const productNode = (subTitle: string[]) => {
    return subTitle.map((st, idx) => {
      const isFirstParameter = idx === 0
      const leftData = RightMenu?.menus?.[0]?.subTitle
      const rightData = RightMenu?.menus?.[1]?.subTitle
      const hasRightData = rightData && rightData[st]
      const reg = /^[✅❌]$/
      const isSame = hasRightData && (rightData[st] !== leftData?.[st] || reg.test(rightData[st]))

      return (
        <div
          key={st}
          className={cn(
            'specs-comparison__row l:flex-col box-border flex w-full overflow-hidden border-t border-t-[#E4E5E6]',
            isFirstParameter && 'specs-comparison__row--first border-t-0'
          )}
        >
          <Heading
            as="h3"
            html={st || ''}
            className="specs-comparison__spec-label l:text-lg min-l:min-w-[216px] min-l:max-w-[216px] min-xl:min-w-[268px] min-xl:max-w-[268px] min-xl:p-6 min-xxl:min-w-[356px] min-xxl:max-w-[356px] min-xxl:text-lg mr-4 flex-1 pt-4 text-base font-bold text-[#000000]"
          />
          <div
            className={cn(
              'specs-comparison__values-grid l:w-full l:gap-4 min-l:gap-8 min-xl:gap-12 min-xl:p-6 min-xxl:gap-16 grid flex-1 place-self-center pb-4 pt-2',
              isSame
                ? 'specs-comparison__values-grid--double grid-cols-2'
                : 'specs-comparison__values-grid--single grid-cols-1'
            )}
          >
            {normalNode(leftData?.[st])}
            {isSame && normalNode(rightData?.[st])}
          </div>
        </div>
      )
    })
  }

  const childrenNode = (node: { subTitle: string[]; isProduct?: boolean }) => {
    const { subTitle } = node
    return productNode(subTitle)
  }

  return (
    <div
      ref={boxRef}
      data-ui-component-id="SpecsComparison"
      className={cn('specs-comparison w-full overflow-hidden bg-[#F5F5F7]', className)}
    >
      <div className="specs-comparison__container container mx-auto">
        {LeftMenu?.data ? (
          <div className="specs-comparison__wrapper min-l:mb-24 min-xl:mb-32 my-16 box-border flex w-full overflow-hidden">
            {LeftMenu.data.length > 1 ? (
              <div className="specs-comparison__sidebar l:hidden min-l:mr-6 min-xl:mr-12 flex">
                <div className="specs-comparison__nav min-l:w-[196px] min-xl:w-[284px] min-xxl:w-[356px] box-border overflow-hidden">
                  {LeftMenu.data.map(item => {
                    return (
                      <div
                        key={item.title}
                        onClick={() => {
                          setSelectItem(item.title)
                          const el = refs.current[item.title]
                          el?.scrollIntoView({ behavior: 'smooth' })
                        }}
                        title={item.title || ''}
                        className={cn(
                          'specs-comparison__nav-item rounded-btn min-xxl:py-4 min-xxl:text-lg box-border cursor-pointer overflow-hidden truncate px-6 py-2 text-base font-bold leading-9 text-[#1D1D1F]',
                          selectItem === item.title && 'specs-comparison__nav-item--active bg-[#FFFFFF]'
                        )}
                      >
                        {item.title || ''}
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}
            <div className="specs-comparison__content box-border flex-1 overflow-hidden">
              <div className="specs-comparison__sections w-full">
                {LeftMenu.data.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="specs-comparison__section w-full overflow-hidden pt-10 first:pt-0"
                      ref={r => {
                        refs.current[item.title] = r
                      }}
                    >
                      {item.title && (
                        <Heading
                          as="h2"
                          className="specs-comparison__section-title min-l:text-[32px] mb-6 text-2xl font-bold"
                          html={item.title || ''}
                        />
                      )}
                      {childrenNode(item)}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
})

SpecsComparison.displayName = 'SpecsComparison'

export default withLayout(SpecsComparison)
